import { normalizar, similaridade } from './texto.js';

/**
 * Detecção de duplicidade no cadastro de produto (RN-022).
 *
 * A regra é deliberadamente um ALERTA, não um bloqueio: dois produtos podem
 * legitimamente ter nomes parecidos, e quem sabe distinguir é a pessoa. O que
 * o sistema não pode fazer é deixar passar em silêncio — cadastro duplicado
 * espalha cotações entre dois registros e some com metade do histórico.
 */

export interface CandidatoProduto {
  id: string;
  nome: string;
  marca: string | null;
  codigos: string[];
}

export type MotivoDuplicidade = 'CODIGO_IGUAL' | 'NOME_SIMILAR';

export interface Duplicidade {
  produtoId: string;
  nome: string;
  motivo: MotivoDuplicidade;
  /** Só presente em NOME_SIMILAR. 0 a 1. */
  similaridade?: number;
  detalhe: string;
}

/**
 * Limiar de similaridade de nome.
 *
 * Calibrado com casos reais, não escolhido no chute. Duas medições que
 * explicam o valor:
 *
 *   "Cabo USB-C 2m"  vs "Cabo USB C 2 metros"  -> 0,73  (É duplicata)
 *   "Fone XPTO 100"  vs "Fone XPTO 200"        -> 0,83  (NÃO é duplicata)
 *
 * Ou seja: similaridade de texto pura ordena esses dois casos ao contrário do
 * que o negócio precisa. Nenhum limiar resolveria — por isso existe a guarda
 * de numerais abaixo, e só depois dela o limiar vale.
 */
export const LIMIAR_SIMILARIDADE = 0.7;

/**
 * Numerais são o que distingue modelo e medida neste domínio: 100 e 200, 2m e
 * 3m, 500ml e 1L. Se os dois nomes trazem números e eles não batem, são
 * produtos diferentes — por mais parecido que o resto do texto seja.
 */
function numerais(texto: string): string {
  return (normalizar(texto).match(/\d+/g) ?? []).sort().join(',');
}

export function detectarDuplicidade(
  novo: { nome: string; marca: string | null; codigos: string[] },
  existentes: readonly CandidatoProduto[],
  limiar = LIMIAR_SIMILARIDADE,
): Duplicidade[] {
  const achados: Duplicidade[] = [];
  const codigosNovos = new Set(novo.codigos.filter((c) => c.trim() !== ''));
  const marcaNova = novo.marca ? normalizar(novo.marca) : null;

  for (const e of existentes) {
    // Código igual é duplicidade certa, independente do nome.
    const igual = e.codigos.find((c) => codigosNovos.has(c));
    if (igual) {
      achados.push({
        produtoId: e.id,
        nome: e.nome,
        motivo: 'CODIGO_IGUAL',
        detalhe: `Já existe um produto com o código ${igual}`,
      });
      continue;
    }

    // Nome parecido só conta com a MESMA marca: "Cabo USB-C 2m" da Anker e da
    // Baseus são produtos diferentes, com nomes quase idênticos.
    const marcaExistente = e.marca ? normalizar(e.marca) : null;
    if (marcaNova === null || marcaExistente === null || marcaNova !== marcaExistente) continue;

    const numNovo = numerais(novo.nome);
    const numExistente = numerais(e.nome);
    if (numNovo !== '' && numExistente !== '' && numNovo !== numExistente) continue;

    const s = similaridade(novo.nome, e.nome);
    if (s >= limiar) {
      achados.push({
        produtoId: e.id,
        nome: e.nome,
        motivo: 'NOME_SIMILAR',
        similaridade: s,
        detalhe: `Nome ${Math.round(s * 100)}% parecido, mesma marca`,
      });
    }
  }

  return achados.sort((a, b) =>
    a.motivo === b.motivo ? (b.similaridade ?? 1) - (a.similaridade ?? 1) : a.motivo === 'CODIGO_IGUAL' ? -1 : 1,
  );
}
